/**
 * Facilitate compound tool — session resume and constellation commit.
 *
 * Actions: resume, commit-constellation
 *
 * Scoring, coaching, and facilitation loop moved to agent-side skills (BET-106).
 * This tool retains only session-state loading and atomic constellation commit.
 * WP-321 E3: Delegates to kernel actions (agentKnowledge.facilitateEnvelope) —
 * MCP layer is a thin passthrough with presentation-only enrichment (cortexUrl).
 *
 * Chain: ENT-37 (Facilitate v1), DEC-56 (PB judges, agents coach),
 *        STA-1 (Constellation Capture Pattern)
 * Rules: BR-8 (session required), BR-22 (single impl), BR-25 (MCP layer),
 *        BR-70 (audit trail)
 */

import { z } from "zod/v3";
import {
  kernelCallEnvelope,
  getWorkspaceContext,
  requireWriteAccess,
  recordSessionActivity,
  getAgentSessionId,
} from "../client.js";
import { runConflictPreflight } from "./conflict-preflight.js";
import { failure, validationResult } from "../envelope.js";

// ── Schema ─────────────────────────────────────────────────────────────────

const FACILITATE_ACTIONS = ["resume", "commit-constellation"] as const;

// WP-465 slice ⑤ (TEN-2439, §3.3): mirrors the kernel's coherencyAcknowledgementValidator
// (convex/lib/gates/coherencyRecord.ts) — response is a free string here; the gate owns the
// vocabulary check (validateCoherencyAcknowledgement), fail-closed, so the connector mirror
// can never drift the SSOT.
const coherencyAcknowledgementSchema = z.object({
  response: z.string().max(200)
    .describe("Acknowledgement response per offender: 'linked' | 'accepted-fix' | 'diverged'."),
  entryId: z.string().max(200).optional()
    .describe("Entry the acknowledgement links to (e.g. the strategic spine entry)."),
  reason: z.string().max(2000).optional()
    .describe("Free-text justification, required for a 'diverged' response."),
  subjectEntryId: z.string().max(200).optional()
    .describe("The refused entry this acknowledgement answers."),
});

export const facilitateSchema = z.object({
  action: z.enum(FACILITATE_ACTIONS).describe(
    "'resume': load session state from an existing bet entry. " +
    "'commit-constellation': atomically accept a bet and all its linked draft entries in one call. Requires betEntryId.",
  ),
  betEntryId: z.string().max(200).optional()
    .describe("Bet entry ID. Required for both actions."),
  operationId: z.string().max(200).optional()
    .describe("Optional idempotency key for commit-constellation retries."),
  // WP-465 slice ⑤: coherency retry controls for a COHERENCY_REFUSED constellation hold.
  // Forwarded verbatim to agentKnowledge.facilitateEnvelope (validation lives at the gate).
  coherencyAcknowledgements: z.array(coherencyAcknowledgementSchema).max(20).optional()
    .describe(
      "Per-offender acknowledgements to retry a constellation held under standard/strict coherency mode. " +
      "Each: {subjectEntryId, response: 'linked' | 'accepted-fix' | 'diverged', entryId?, reason?}.",
    ),
  steeringOverrideReason: z.string().max(2000).optional()
    .describe(
      "Typed override reason (>= 12 chars) to push a constellation past a coherency hold instead of acknowledging.",
    ),
});

// ── Types ──────────────────────────────────────────────────────────────────

interface SessionDraft {
  entryId: string;
  name: string;
  collection: string;
  relationType: string;
  status: string;
}

interface ConstellationState {
  elementCount: number;
  riskCount: number;
  decisionCount: number;
}

interface BetEntryData {
  betEntryId: string;
  name: string;
  status: string;
  data: Record<string, unknown>;
}

interface ResumeData {
  betEntry: BetEntryData;
  constellation: ConstellationState;
  sessionDrafts: SessionDraft[];
}

interface ConstellationCommitResult {
  operationId: string;
  committedIds: string[];
  alreadyCommittedIds: string[];
  proposedIds: string[];
  failedIds: Array<{ entryId: string; name: string; error: string }>;
  conflictIds: Array<{ entryId: string; name: string; reason: string }>;
  totalRelations: number;
  proposalCreated?: boolean;
}

interface CommitConstellationData {
  result: ConstellationCommitResult;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function buildCortexUrl(workspaceSlug: string, entryId: string): string {
  const appUrl = process.env.PRODUCTBRAIN_APP_URL ?? "https://work.productbrain.io";
  return `${appUrl}/${workspaceSlug}/legacy/entries/${entryId}`;
}

// WP-484 S3 (clean break, TEN-2157): the standalone `facilitate` tool
// retired here. handleResume / handleCommitConstellation stay live —
// session.ts (the `session` compound, action=resume / action=commit-constellation)
// is their only remaining caller.

// ── Action Handlers ────────────────────────────────────────────────────────

export async function handleResume(args: z.infer<typeof facilitateSchema>) {
  const betId = args.betEntryId;
  if (!betId) {
    return validationResult("`betEntryId` is required for resume action.");
  }

  const { workspaceId, workspaceSlug } = await getWorkspaceContext();

  // Delegate to kernel action
  const envelope = await kernelCallEnvelope<ResumeData>("agentKnowledge.facilitateEnvelope", {
    workspaceId,
    action: "resume",
    betEntryId: betId,
  });

  // MCP-layer enrichment: add cortexUrl (kernel doesn't build Cortex URLs — that's MCP presentation)
  const enrichedData = {
    ...envelope.data,
    cortexUrl: buildCortexUrl(workspaceSlug, betId),
  };

  // Rebuild rich markdown briefing (presentation concern — kernel stays semantic)
  const bet = enrichedData.betEntry;
  const cx = enrichedData.constellation;
  const constellationParts: string[] = [];
  if (cx.elementCount > 0) constellationParts.push(`${cx.elementCount} element(s)`);
  if (cx.riskCount > 0) constellationParts.push(`${cx.riskCount} risk(s)`);
  if (cx.decisionCount > 0) constellationParts.push(`${cx.decisionCount} decision(s)`);
  const constellationSummary = constellationParts.length > 0
    ? constellationParts.join(", ")
    : "no entries captured yet";

  const draftLines = enrichedData.sessionDrafts.slice(0, 10).map(
    (d: SessionDraft) => `- \`${d.entryId}\` ${d.name} [${d.collection}] (${d.status})`,
  );
  const textLines = [
    `# ${bet.name}`,
    "",
    `**ID:** \`${bet.betEntryId}\` · **Status:** ${bet.status}`,
    enrichedData.cortexUrl ? `**Cortex:** ${enrichedData.cortexUrl}` : "",
    "",
    `**Constellation:** ${constellationSummary}`,
    ...(draftLines.length > 0 ? ["", "**Session drafts:**", ...draftLines] : []),
  ].filter((l) => l !== undefined);

  return {
    content: [{ type: "text" as const, text: textLines.join("\n") }],
    structuredContent: {
      ok: true as const,
      summary: envelope.summary,
      data: enrichedData,
      next: envelope.next,
      _meta: envelope._meta,
    },
  };
}

// ── Commit Constellation ────────────────────────────────────────────────

export async function handleCommitConstellation(args: z.infer<typeof facilitateSchema>) {
  requireWriteAccess();

  const betId = args.betEntryId;
  if (!betId) {
    return validationResult("`betEntryId` is required for commit-constellation action.");
  }

  const { workspaceId } = await getWorkspaceContext();
  const sessionId = getAgentSessionId();
  const operationId = args.operationId ?? `${betId}:${sessionId ?? "sessionless"}:${Date.now()}`;

  // ── MCP-layer conflict preflight (pre-condition check, stays in MCP layer) ──
  // We need the bet name and description for the conflict check. Load minimally.
  let betName = betId;
  let betDesc = "";
  let betCollectionHint: string | undefined;
  try {
    type BetEntryShape = {
      name: string;
      data?: Record<string, unknown>;
      collectionSlug?: string;
      collection?: { slug?: string; name?: string };
    };
    const betEnvelope = await kernelCallEnvelope<BetEntryShape>("chain.getEntry", { entryId: betId });
    const entry = betEnvelope.data;
    if (entry) {
      betName = entry.name ?? betId;
      const d = entry.data ?? {};
      const links = d["links"] as Record<string, unknown> | undefined;
      betDesc = (d.problem as string) ?? (links?.problem as string) ?? (d.description as string) ?? "";
      betCollectionHint = entry.collectionSlug ?? entry.collection?.slug ?? entry.collection?.name;
    }
  } catch {
    // If we can't load the bet entry, the kernel will handle the NOT_FOUND case
  }

  const preflight = await runConflictPreflight(betName, betDesc, betCollectionHint);

  if (preflight.blockingContradictions.length > 0) {
    await recordSessionActivity({ contradictionWarning: true });
    const conflictLines = preflight.blockingContradictions
      .map((c) => `- ${c.chainEntryName} (${c.chainEntryId}) — ${c.explanation}`)
      .join("\n");

    return {
      content: [{
        type: "text" as const,
        text: [
          `# Accept blocked: contradiction detected`,
          "",
          `\`${betId}\` matches ${preflight.blockingContradictions.length} high-confidence contradiction(s). Resolve the conflict before accepting the constellation.`,
          "",
          conflictLines,
        ].join("\n"),
      }],
      structuredContent: failure(
        "CONTRADICTION_BLOCKED",
        `${preflight.blockingContradictions.length} high-confidence contradiction(s) found.`,
        "Review the conflicting entry or entries before retrying the accept.",
        undefined,
        {
          betEntryId: betId,
          source: preflight.source,
          blockingContradictions: preflight.blockingContradictions,
          advisoryWarnings: preflight.advisoryWarnings,
        },
      ),
    };
  }

  if (preflight.source === "semantic" ? preflight.conflicts.length > 0 : preflight.advisoryWarnings.length > 0) {
    await recordSessionActivity({ contradictionWarning: true });
  }

  // ── Delegate to kernel action ───────────────────────────────────────────
  let envelope: Awaited<ReturnType<typeof kernelCallEnvelope<CommitConstellationData>>>;
  try {
    envelope = await kernelCallEnvelope<CommitConstellationData>("agentKnowledge.facilitateEnvelope", {
      workspaceId,
      action: "commit-constellation",
      betEntryId: betId,
      operationId,
      sessionId: sessionId ?? undefined,
      author: sessionId ? `agent:${sessionId}` : "kernel:facilitate",
      // WP-465 slice ⑤ (TEN-2439, §3.7): relay the coherency retry controls so a
      // COHERENCY_REFUSED constellation hold is actually retryable through MCP.
      ...(args.coherencyAcknowledgements ? { coherencyAcknowledgements: args.coherencyAcknowledgements } : {}),
      ...(args.steeringOverrideReason ? { steeringOverrideReason: args.steeringOverrideReason } : {}),
    });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    const code = (err as { code?: string })?.code;
    const diagnostics = (err as { diagnostics?: Record<string, unknown> })?.diagnostics;

    // WP-465 slice ⑤ (TEN-2439, §3.7): a coherency HOLD is NOT a backend error. The
    // kernel returns an `ok:false` COHERENCY_REFUSED envelope naming each offender and
    // its routes; callGateway throws on `ok:false`, so detect the code here — BEFORE the
    // generic wrapper — and surface the structured `coherencyRefusals` (subject ids +
    // routes) so the agent can populate the retry controls (coherencyAcknowledgements /
    // steeringOverrideReason). Mirrors the kernel facilitate envelopeError shape and the
    // commit/update single-entry refusal tools — same code, same diagnostics key.
    if (code === "COHERENCY_REFUSED") {
      const coherencyRefusals = (Array.isArray(diagnostics?.coherencyRefusals)
        ? diagnostics.coherencyRefusals
        : []) as Array<{ entryId: string; name: string; refusal: Record<string, unknown> }>;
      const offenderLines = coherencyRefusals
        .map((r) => `- \`${r.entryId}\` ${r.name}: ${String((r.refusal as { reason?: string })?.reason ?? "coherency challenge unsatisfied")}`)
        .join("\n");
      const count = coherencyRefusals.length;
      return {
        content: [{
          type: "text" as const,
          text: [
            `# Constellation held by the coherency gate`,
            "",
            `\`${betId}\` was not accepted — ${count} entr${count === 1 ? "y" : "ies"} refused. Nothing was committed; drafts persist.`,
            ...(offenderLines ? ["", offenderLines] : []),
          ].join("\n"),
        }],
        structuredContent: failure(
          "COHERENCY_REFUSED",
          `Constellation held: ${count} entr${count === 1 ? "y" : "ies"} refused by the coherency gate — nothing was committed, drafts persist.`,
          "Respond per offender and retry commit-constellation: link the entry to the strategic spine, pass coherencyAcknowledgements [{subjectEntryId, response: 'linked' | 'accepted-fix' | 'diverged', entryId?, reason?}], or supply a typed steeringOverrideReason (at least 12 characters).",
          [{ tool: "session", description: "Retry with coherency acknowledgements", parameters: { action: "commit-constellation", betEntryId: betId } }],
          { coherencyRefusals },
        ),
      };
    }

    return {
      content: [{
        type: "text" as const,
        text: `# Bet accept failed\n\n\`${betId}\` could not be accepted: ${msg}\n\nNo constellation entries were accepted.`,
      }],
      structuredContent: failure(
        "BACKEND_ERROR",
        `Bet accept failed: ${msg}`,
        "Retry or check for accept blockers.",
        [{ tool: "session", description: "Resume session", parameters: { action: "resume", betEntryId: betId } }],
        diagnostics,
      ),
    };
  }

  // Advisory: record session activity (best-effort, non-blocking)
  await recordSessionActivity({ contradictionWarning: preflight.blockingContradictions.length > 0 });
  // Per-entry audit trail — record each committed doc in session activity (BR-70)
  for (const docId of (envelope.data?.result?.committedIds ?? [])) {
    try {
      await recordSessionActivity({ entryModified: docId });
    } catch {
      // Advisory — non-blocking
    }
  }

  // Build text output from kernel result
  const result = envelope.data.result;
  const suggestions = { conflictPreflightSource: preflight.source };

  const lines: string[] = [];
  const hasIssues = (result.failedIds?.length ?? 0) > 0 || (result.conflictIds?.length ?? 0) > 0;
  if (result.proposalCreated) {
    lines.push(
      hasIssues ? `# Proposal created with issues` : `# Proposal created`,
      "",
      `Workspace uses consent-based governance. \`${betId}\` was submitted as a proposal — it will be accepted when approved.`,
      `No linked entries were accepted because the bet itself is still awaiting approval.`,
    );
  } else {
    lines.push(
      hasIssues ? `# Accepted with issues` : `# Accepted`,
      "",
      `**${result.committedIds?.length ?? 0} entries** accepted onto the Chain, **${result.totalRelations} connections** preserved.`,
      hasIssues
        ? `\`${betId}\` was partially accepted. Review conflicts/failed entries below before considering the constellation complete.`
        : `\`${betId}\` and its full constellation are now source of truth.`,
    );
  }

  if (preflight.source === "semantic" && preflight.conflicts.length > 0) {
    lines.push(
      "",
      `⚠ **Semantic conflict preflight** (advisory):`,
      ...preflight.conflicts.map((c) => `- \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`),
    );
  } else if (preflight.source === "heuristic" && preflight.advisoryWarnings.length > 0) {
    lines.push(
      "",
      `⚠ **Contradiction warnings** (advisory):`,
      ...preflight.advisoryWarnings.map(
        (w) => `- \`${w.entryId}\` ${w.name} [${w.collection}] (${w.governsCount} governs relation${w.governsCount === 1 ? "" : "s"})`,
      ),
    );
  }

  if ((result.committedIds?.length ?? 0) > 0) {
    lines.push("", `**Accepted:** ${result.committedIds.join(", ")}`);
  }
  if ((result.alreadyCommittedIds?.length ?? 0) > 0) {
    lines.push("", `**Already accepted:** ${result.alreadyCommittedIds.join(", ")}`);
  }
  if ((result.proposedIds?.length ?? 0) > 0) {
    lines.push("", `**Proposed (not yet accepted):** ${result.proposedIds.join(", ")}`);
  }
  if ((result.conflictIds?.length ?? 0) > 0) {
    lines.push("", `**${result.conflictIds.length} conflicts:**`);
    for (const c of result.conflictIds) {
      lines.push(`- \`${c.entryId}\` ${c.name}: ${c.reason}`);
    }
  }
  if ((result.failedIds?.length ?? 0) > 0) {
    lines.push("", `**${result.failedIds.length} failed:**`);
    for (const f of result.failedIds) {
      lines.push(`- \`${f.entryId}\` ${f.name}: ${f.error}`);
    }
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: {
      ok: true as const,
      summary: envelope.summary,
      data: { result, suggestions },
      next: envelope.next,
      _meta: envelope._meta,
    },
  };
}
